import { useEffect, useRef, useState } from "react";
import { ArrowsClockwise, Cube } from "@phosphor-icons/react";

/**
 * Box3D — a CSS-transform-based 3D box preview.
 * Renders a box with dimensions L × W × H (mm) with drag-to-rotate and
 * an optional logo overlaid on the front face.
 *
 * Templates that produce an "open" top or bottom (tray, mailer, sleeve) hide
 * the corresponding face so users see the actual assembled shape.
 */
const OPEN_FACES = {
  "tray-box": ["top"],
  "sleeve":   ["top", "bottom"],
  "mailer-box": [],       // closed with tuck
  "pillow-box": ["top", "bottom"],
  "straight-tuck-end": [],
  "reverse-tuck-end": [],
  "auto-lock-bottom": [],
  "snap-lock-bottom": [],
};

const FACE_COLOR = {
  front:  "#f8fafc",
  back:   "#f1f5f9",
  left:   "#e2e8f0",
  right:  "#e2e8f0",
  top:    "#f8fafc",
  bottom: "#cbd5e1",
};

export default function Box3D({ L, W, H, templateSlug, logoDataUrl }) {
  const [rot, setRot] = useState({ x: -20, y: -30 });
  const [zoom, setZoom] = useState(1);
  const [autoSpin, setAutoSpin] = useState(false);
  const dragging = useRef(null);

  // Fit box into the ~500px viewport
  const maxDim = Math.max(L, W, H);
  const scale = Math.min(1, 340 / maxDim) * zoom;

  useEffect(() => {
    if (!autoSpin) return;
    let raf;
    const tick = () => {
      setRot(r => ({ ...r, y: r.y + 0.4 }));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [autoSpin]);

  const onDown = (e) => {
    const point = e.touches ? e.touches[0] : e;
    dragging.current = { x: point.clientX, y: point.clientY, rx: rot.x, ry: rot.y };
    setAutoSpin(false);
  };
  const onMove = (e) => {
    if (!dragging.current) return;
    const point = e.touches ? e.touches[0] : e;
    const dx = point.clientX - dragging.current.x;
    const dy = point.clientY - dragging.current.y;
    setRot({
      x: Math.max(-88, Math.min(88, dragging.current.rx - dy * 0.5)),
      y: dragging.current.ry + dx * 0.5,
    });
  };
  const onUp = () => { dragging.current = null; };

  useEffect(() => {
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openFaces = OPEN_FACES[templateSlug] ?? [];

  const face = (name, w, h, transform, extraStyle = {}) => {
    if (openFaces.includes(name)) return null;
    return (
      <div
        key={name}
        data-testid={`3d-face-${name}`}
        style={{
          position: "absolute",
          width: `${w}px`,
          height: `${h}px`,
          left: "50%",
          top: "50%",
          marginLeft: `${-w / 2}px`,
          marginTop: `${-h / 2}px`,
          transform,
          background: FACE_COLOR[name],
          border: "1.5px solid #64748b",
          boxShadow: "inset 0 0 20px rgba(0,0,0,0.05)",
          transformStyle: "preserve-3d",
          backfaceVisibility: "visible",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          ...extraStyle,
        }}
      >
        {name === "front" && logoDataUrl && (
          <img
            src={logoDataUrl}
            alt="logo"
            style={{ maxWidth: "60%", maxHeight: "60%", objectFit: "contain", opacity: 0.9 }}
          />
        )}
        {!logoDataUrl && (
          <span
            style={{
              fontSize: "10px",
              fontWeight: 800,
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              color: "#94a3b8",
            }}
          >{name}</span>
        )}
      </div>
    );
  };

  // Face positioning in 3D:
  // X: left-right (L axis)
  // Y: up-down    (H axis, DOM y-axis points down so we flip)
  // Z: depth      (W axis)
  return (
    <div className="w-full flex flex-col items-center">
      <div
        className="relative w-full max-w-[560px] h-[480px] flex items-center justify-center select-none cursor-grab active:cursor-grabbing"
        style={{ perspective: "1400px" }}
        onMouseDown={onDown}
        onTouchStart={onDown}
        data-testid="box3d-viewport"
      >
        <div
          style={{
            position: "relative",
            width: "1px",
            height: "1px",
            transformStyle: "preserve-3d",
            transform: `scale(${scale}) rotateX(${rot.x}deg) rotateY(${rot.y}deg)`,
            transition: dragging.current || autoSpin ? "none" : "transform 0.2s ease-out",
          }}
        >
          {face("front",  L, H, `translateZ(${W / 2}px)`)}
          {face("back",   L, H, `translateZ(${-W / 2}px) rotateY(180deg)`)}
          {face("left",   W, H, `translateX(${-L / 2}px) rotateY(-90deg)`)}
          {face("right",  W, H, `translateX(${L / 2}px) rotateY(90deg)`)}
          {face("top",    L, W, `translateY(${-H / 2}px) rotateX(90deg)`)}
          {face("bottom", L, W, `translateY(${H / 2}px) rotateX(-90deg)`)}

          {/* Floor shadow */}
          <div
            style={{
              position: "absolute",
              width: `${L * 1.4}px`,
              height: `${W * 1.4}px`,
              left: "50%",
              top: "50%",
              marginLeft: `${-L * 0.7}px`,
              marginTop: `${-W * 0.7}px`,
              transform: `translateY(${H / 2 + 8}px) rotateX(90deg)`,
              background: "radial-gradient(ellipse at center, rgba(0,0,0,0.22) 0%, transparent 65%)",
              pointerEvents: "none",
            }}
          />
        </div>
      </div>

      {/* Controls */}
      <div className="mt-4 flex items-center gap-2 flex-wrap justify-center">
        <button
          onClick={() => setAutoSpin(!autoSpin)}
          data-testid="3d-spin"
          className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest rounded border ${autoSpin ? "bg-[var(--pf-brand)] text-white border-[var(--pf-brand)]" : "border-[var(--pf-border)] hover:border-[var(--pf-brand)]"}`}
        >
          <ArrowsClockwise size={12} weight="bold" className="inline mr-1"/>
          {autoSpin ? "Spinning" : "Auto-spin"}
        </button>
        <button
          onClick={() => setRot({ x: -20, y: -30 })}
          data-testid="3d-reset"
          className="px-3 py-1.5 text-xs font-bold uppercase tracking-widest rounded border border-[var(--pf-border)] hover:border-[var(--pf-brand)]"
        >
          <Cube size={12} weight="bold" className="inline mr-1"/>Reset view
        </button>
        <div className="flex items-center gap-2 text-xs text-[var(--pf-text-2)] pl-3 border-l border-[var(--pf-border)]">
          Zoom
          <input
            type="range" min="50" max="180" value={zoom * 100}
            data-testid="3d-zoom"
            onChange={e => setZoom(Number(e.target.value) / 100)}
            className="accent-[var(--pf-brand)] w-24"
          />
        </div>
      </div>
      <p className="mt-2 text-[10px] text-[var(--pf-text-3)] tracking-wider uppercase">
        Drag to rotate · Scroll the zoom slider · Preview only — not affected by material thickness
      </p>
    </div>
  );
}
